import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { LogIn, ShieldCheck, Package, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-brand-muted">Loading…</div>;
  if (user) return <Navigate to={location.state?.from || "/account"} replace />;

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src="https://images.pexels.com/photos/8751528/pexels-photo-8751528.jpeg" alt="Bindni jewellery" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-maroon-deep/70 to-transparent" />
        <div className="absolute bottom-0 p-12 text-brand-ivory">
          <p className="label-tiny text-brand-gold">The Bindni Circle</p>
          <h2 className="font-serif-brand text-4xl lg:text-5xl mt-3 leading-tight">Welcome to your private atelier</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 lg:px-16 py-16">
        <div className="w-full max-w-md">
          <p className="label-tiny text-brand-gold">Sign In · Sign Up</p>
          <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">Enter the Royal Circle</h1>
          <p className="text-brand-muted mt-3 leading-relaxed">Sign in with Google to save addresses, track your orders, and sync your wishlist across devices.</p>

          <button onClick={startLogin} className="mt-10 w-full bg-brand-charcoal text-brand-ivory py-4 px-6 flex items-center justify-center gap-3 hover:bg-brand-maroon transition-colors duration-200" data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.1z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.6-4.5 2.6-7.2 2.6-5.2 0-9.6-3.4-11.2-8l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <span className="text-sm tracking-[0.2em] uppercase font-medium">Continue with Google</span>
          </button>

          <p className="text-xs text-brand-muted mt-4 text-center">By continuing, you agree to our Terms & Privacy Policy.</p>

          <div className="gold-divider my-10" />

          <div className="space-y-4 text-sm">
            {[
              { icon: Package, l: "Order history & one-tap reorder" },
              { icon: Heart, l: "Wishlist synced across devices" },
              { icon: ShieldCheck, l: "Save addresses for faster checkout" },
              { icon: LogIn, l: "No password to remember — Google secure" },
            ].map((f) => (
              <div key={f.l} className="flex items-center gap-3 text-brand-charcoal">
                <f.icon size={16} strokeWidth={1.4} className="text-brand-maroon" /> {f.l}
              </div>
            ))}
          </div>

          <p className="text-xs text-brand-muted mt-10 text-center">
            Prefer to browse as guest? <a href="/shop" className="text-brand-maroon hover:underline">Continue shopping</a>
          </p>
        </div>
      </div>
    </div>
  );
}
